import { formatCurrency as formatCurrencyShared } from "@wtc/ui/lib/currency";
/**
 * Shared formatting utilities for QuickBooks invoice data
 * Used across multiple components to maintain consistency and eliminate duplication
 */

/**
 * Format a number as USD currency
 * @param amount - The amount to format
 * @returns Formatted currency string or "N/A" if amount is undefined/null
 */
export const formatCurrency = (amount?: number): string => {
  if (amount === undefined || amount === null) return "N/A";
  return formatCurrencyShared(amount, { currency: "USD", fallback: "", minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/**
 * Format a date string to a readable date format
 * Handles various date formats and extracts YYYY-MM-DD pattern
 * @param dateString - The date string to format
 * @returns Formatted date string (e.g., "January 15, 2024") or "N/A" if invalid
 */
export const formatDate = (dateString?: string): string => {
  if (!dateString) return "N/A";
  try {
    // Extract just the date part (YYYY-MM-DD) from various formats
    const dateMatch = dateString.match(/(\d{4})-(\d{2})-(\d{2})/);
    if (dateMatch) {
      const [, yearStr, monthStr, dayStr] = dateMatch;
      const year = parseInt(yearStr, 10);
      const month = parseInt(monthStr, 10);
      const day = parseInt(dayStr, 10);
      const date = new Date(year, month - 1, day);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } else {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    }
  } catch {
    return dateString;
  }
};

/**
 * Format a date string to include both date and time
 * @param dateString - The date string to format
 * @returns Formatted date-time string (e.g., "Jan 15, 2024, 02:30 PM") or "N/A" if invalid
 */
export const formatDateTime = (dateString?: string): string => {
  if (!dateString) return "N/A";
  try {
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return dateString;
  }
};
